import type { AdminSession } from "@kontextwerk/control-web-client"
import {
  buildPermissionState,
  type PermissionDefinition,
  type PermissionLevel,
  type PermissionState,
} from "@kontextwerk/control-web-frame/logic/permissions/model"

export type { PermissionState }

export type EmployeePermissionCapability = "denied" | "restricted" | "all"

export type EmployeePermissionAccess = {
  capability: EmployeePermissionCapability
  employeeIds: string[] | null
}

export const TERMIN_PERMISSION_DEFINITIONS: PermissionDefinition[] = [
  { key: "termin.scheduling", scopes: ["own", "all"] },
]

export const buildAppPermissionState = (
  session: AdminSession | null | undefined,
): PermissionState => buildPermissionState(session, { definitions: TERMIN_PERMISSION_DEFINITIONS })

/**
 * Resolve the widest scope for the given permission key and level and convert it
 * into an allowed employee id list that can be used to filter UI selections.
 *
 * Returns `null` only for full access, an empty array for denied access, or a
 * single-entry array containing the current user id for assigned/own access.
 */
export const resolveEmployeePermissionAccess = (
  permissions: PermissionState,
  key: string,
  currentUserId: string | null | undefined,
  level: PermissionLevel = "view",
): EmployeePermissionAccess => {
  if (permissions.isSuper) return { capability: "all", employeeIds: null }

  if (!permissions.allows(key, level)) {
    return { capability: "denied", employeeIds: [] }
  }

  const scope = permissions.highestScope(key, level)

  if (scope === "all") return { capability: "all", employeeIds: null }

  if (!scope || !currentUserId) {
    return { capability: "denied", employeeIds: [] }
  }

  return { capability: "restricted", employeeIds: [currentUserId] }
}

export const resolveAllowedEmployeeIds = (
  permissions: PermissionState,
  key: string,
  currentUserId: string | null | undefined,
  level: PermissionLevel = "view",
): string[] | null =>
  resolveEmployeePermissionAccess(permissions, key, currentUserId, level).employeeIds
